import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchMostRecentReportId } from '../../lib/llnAssessment/llnReports';
import { supabase } from '../../lib/supabase/supabaseClient';

// Admin menu structure with nested categories
interface AdminMenuItem {
  label: string;
  path?: string;
  children?: AdminMenuItem[];
}

const adminMenuStructure: AdminMenuItem[] = [
  {
    label: 'System',
    children: [
      { label: 'User Management', path: '/admin/users' },
      { label: 'Performance Dashboard', path: '/admin/performance' },
      { label: 'Session Timeout', path: '/admin/session-timeout' },
      { label: 'Site Pages', path: '/admin/site-pages' },
    ]
  },
  {
    label: 'Digital Readiness',
    children: [
      { label: 'View Reports', path: '/admin/user-reports' },
      { label: 'Questions', path: '/admin/questions' },
      { label: 'Categories', path: '/admin/categories' },
      { label: 'Recommendations', path: '/admin/recommendations' },
      { label: 'Response Values', path: '/admin/response-values' },
      { label: 'Attempts Config', path: '/admin/digital-readiness-config' },
      { label: 'User Attempts', path: '/admin/digital-readiness-user-attempts' },
    ]
  },
  {
    label: 'LLN Assessment',
    children: [
      { label: 'View Reports', path: '/admin/lln-reports' },
      { label: 'Manage Questions', path: '/admin/lln-questions' },
      { label: 'Mark Written Responses', path: '/admin/written-responses' },
      { label: 'AI Review Queue', path: '/admin/lln-manual-review' },
      { label: 'AI Dashboard', path: '/admin/lln-ai-dashboard' },
      { label: 'Assessment Config', path: '/admin/lln-config' },
      { label: 'User Attempts', path: '/admin/lln-user-attempts' },
      { label: 'Manage Rubrics', path: '/admin/lln-rubrics' },
      { label: 'System Diagnostics', path: '/admin/lln-diagnostics' },
      { label: 'MCP Prompt Templates', path: '/admin/mcp-prompt-templates' },
      { label: 'ACSF Core Skills', path: '/admin/acsf-core-skills' },
    ]
  },
  {
    label: 'Forms',
    children: [
      { label: 'View Reports', path: '/admin/lln-induction-forms' },
      { label: 'Form Templates', path: '/admin/lln-induction-form-templates' },
      { label: 'Course Management', path: '/admin/lln-courses' },
    ]
  }
];

const Navbar: React.FC = () => {
  const { user, signOut, userRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const [hasRoles, setHasRoles] = useState(false); // Track if user has any roles
  const [isInvited, setIsInvited] = useState(false); // Track if user only has 'invited' role
  
  // Initialize expanded categories from localStorage
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('adminMenuExpandedCategories');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  const profileMenuRef = useRef<HTMLDivElement>(null);
  const adminMenuRef = useRef<HTMLDivElement>(null);

  // OPTIMIZATION: Use cached userRole from AuthContext instead of making separate DB calls
  // Derive isAdmin from cached userRole in AuthContext
  const isAdmin = userRole === 'admin';

  // OPTIMIZATION: Derive hasRoles and isInvited from cached userRole
  // No need for additional database queries - AuthContext already has this information
  useEffect(() => {
    if (!user) {
      setHasRoles(false);
      setIsInvited(false);
      return;
    }

    // If user has a role from AuthContext (not 'guest'), they have roles
    if (userRole !== 'guest') {
      setHasRoles(true);
      // Note: We can't determine 'invited' status from userRole alone
      // since AuthContext only tracks 'admin' vs 'user'
      // For now, assume users with roles are not just invited
      setIsInvited(false);
    } else {
      setHasRoles(false);
      setIsInvited(false);
    }
  }, [user?.id, userRole]);


  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isProfileMenuOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setIsProfileMenuOpen(false);
      }
      
      if (
        isAdminMenuOpen &&
        adminMenuRef.current &&
        !adminMenuRef.current.contains(event.target as Node)
      ) {
        setIsAdminMenuOpen(false);
      }
      
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen, isAdminMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
    setIsAdminMenuOpen(false);
  };

  const toggleAdminMenu = () => {
    setIsAdminMenuOpen(!isAdminMenuOpen);
    setIsProfileMenuOpen(false);
  };
  

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Close menus when a link is clicked (but preserve expanded categories)
  const handleAdminLinkClick = () => {
    setIsAdminMenuOpen(false);
    // Don't clear expandedCategories - let them persist
  };

  const handleProfileLinkClick = () => {
    setIsProfileMenuOpen(false);
  };

  const handleMobileLinkClick = () => {
    setIsMenuOpen(false);
  };

  const toggleCategory = (categoryLabel: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryLabel)) {
        newSet.delete(categoryLabel);
      } else {
        newSet.add(categoryLabel);
      }
      
      // Save to localStorage
      try {
        localStorage.setItem('adminMenuExpandedCategories', JSON.stringify(Array.from(newSet)));
      } catch (error) {
        console.error('Failed to save menu state:', error);
      }
      
      return newSet;
    });
  };

  return (
    <nav className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="relative flex justify-between h-16">
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            {/* Mobile menu button */}
            <button
              type="button"
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold text-primary">SMIT</span>
                <span className="ml-1 text-2xl font-medium text-gray-600">LLN</span>
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-8">
              {/* Desktop nav links */}
              <Link
                to="/"
                className={`${
                  isActive('/') && !isActive('/dashboard')
                    ? 'border-primary text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16`}
              >
                Home
              </Link>
              
              {/* Home link is always visible */}
              
              {user && (
                <>
                  {/* Only show these links if user has roles and is not just invited */}
                  {hasRoles && !isInvited && (
                    <>
                      {/* User Dashboard Link */}
                      <Link
                        to="/user/dashboard"
                        className={`${
                          isActive('/user/dashboard')
                            ? 'border-primary text-gray-900'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16`}
                      >
                        Dashboard
                      </Link>
                      
                    </>
                  )}
                  
                  {/* Admin Dashboard Link - only visible for admins */}
                  {isAdmin && (
                    <Link
                      to="/admin/dashboard"
                      className={`${
                        isActive('/admin/dashboard')
                          ? 'border-primary text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-16`}
                    >
                      Admin
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            {user ? (
              <div className="flex items-center space-x-4">
                {/* Admin button - only visible for admins */}
                {isAdmin && (
                  <div className="relative" ref={adminMenuRef}>
                    <button
                      onClick={toggleAdminMenu}
                      className="bg-white p-1 rounded-full text-gray-500 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      <span className="sr-only">Admin Menu</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </button>
                    
                    {isAdminMenuOpen && (
                      <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 max-h-[80vh] overflow-y-auto">
                        {adminMenuStructure.map((category, idx) => (
                          <div key={category.label}>
                            {idx > 0 && <div className="border-t border-gray-100"></div>}
                            
                            {/* Desktop: Click to expand categories */}
                            <div className="relative">
                              <button
                                onClick={() => toggleCategory(category.label)}
                                className="w-full px-4 py-2.5 text-xs font-semibold text-gray-700 uppercase tracking-wider flex items-center justify-between hover:bg-gray-50 transition-colors"
                              >
                                <span>{category.label}</span>
                                <svg
                                  className={`w-3.5 h-3.5 text-gray-400 transition-transform ${
                                    expandedCategories.has(category.label) ? 'rotate-90' : ''
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                              
                              {/* Expanded submenu items */}
                              {expandedCategories.has(category.label) && category.children && (
                                <div className="bg-gray-50 border-t border-gray-100">
                                  {category.children.map((item) => (
                                    <Link
                                      key={item.path}
                                      to={item.path!}
                                      onClick={handleAdminLinkClick}
                                      className={`${
                                        isActive(item.path!)
                                          ? 'bg-blue-50 text-blue-700 font-medium border-l-2 border-blue-500'
                                          : 'text-gray-700 hover:bg-gray-100 border-l-2 border-transparent'
                                      } block px-6 py-2 text-sm transition-colors`}
                                    >
                                      {item.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Profile menu - only visible for users with roles (not just invited) */}
                {hasRoles && !isInvited && (
                  <div className="relative" ref={profileMenuRef}>
                    <button
                      onClick={toggleProfileMenu}
                      className="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      <span className="sr-only">Open user menu</span>
                      <div className="h-8 w-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg">
                        {user.email?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    </button>
                    
                    {isProfileMenuOpen && (
                      <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                        <div className="px-4 py-2 text-sm text-gray-700 border-b">
                          Signed in as <span className="font-medium">{user.email}</span>
                        </div>
                        <Link
                          to="/profile"
                          onClick={handleProfileLinkClick}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Your Profile
                        </Link>
                        <button
                          onClick={handleSignOut}
                          className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Sign out
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Sign out button for users without roles or only with invited role */}
                {(!hasRoles || isInvited) && (
                  <button
                    onClick={handleSignOut}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                  >
                    Sign out
                  </button>
                )}
              </div>
            ) : (
              <div className="flex space-x-4">
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
                >
                  Log in
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} sm:hidden bg-white z-10 absolute w-full top-16 shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto`}>
        <div className="pt-2 pb-4 space-y-1">
          <Link
            to="/"
            className={`${
              isActive('/') && !isActive('/dashboard')
                ? 'bg-primary-50 border-primary text-primary'
                : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
            } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
          >
            Home
          </Link>
          
          {user && (
            <>
              {/* Only show these links if user has roles and is not just invited */}
              {hasRoles && !isInvited && (
                <>
                  {/* Mobile Dashboard Links */}
                  <Link
                    to="/user/dashboard"
                    onClick={handleMobileLinkClick}
                    className={`${
                      isActive('/user/dashboard')
                        ? 'bg-primary-50 border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                    } block pl-3 pr-4 py-2 border-l-4 text-base font-medium`}
                  >
                    Dashboard
                  </Link>
                  
                  {/* Mobile Admin Section - Accordion Style */}
                  {isAdmin && (
                    <div className="border-t border-gray-200 pt-4 pb-2">
                      <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Admin
                      </h3>
                      <div className="mt-1 space-y-1">
                        {adminMenuStructure.map((category) => (
                          <div key={category.label}>
                            {/* Category Header - Accordion Toggle */}
                            <button
                              onClick={() => toggleCategory(category.label)}
                              className="w-full flex items-center justify-between pl-6 pr-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              <span>{category.label}</span>
                              <svg
                                className={`w-4 h-4 transition-transform ${expandedCategories.has(category.label) ? 'rotate-90' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                            
                            {/* Category Items - Collapsible */}
                            {expandedCategories.has(category.label) && category.children && (
                              <div className="bg-gray-50">
                                {category.children.map((item) => (
                                  <Link
                                    key={item.path}
                                    to={item.path!}
                                    onClick={handleMobileLinkClick}
                                    className={`${
                                      isActive(item.path!)
                                        ? 'bg-primary-50 text-primary-700 border-l-4 border-primary'
                                        : 'text-gray-600 border-l-4 border-transparent'
                                    } block pl-10 pr-4 py-2 text-sm hover:bg-gray-100`}
                                  >
                                    {item.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Mobile Profile Section */}
                  <div className="border-t border-gray-200 pt-4 pb-2">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Your Account
                    </h3>
                    <div className="mt-1 space-y-1">
                      <Link
                        to="/profile"
                        onClick={handleMobileLinkClick}
                        className={`${
                          isActive('/profile')
                            ? 'bg-primary-50 border-primary text-primary'
                            : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                        } block pl-6 pr-4 py-2 border-l-4 text-sm font-medium`}
                      >
                        Your Profile
                      </Link>
                      <button
                        onClick={() => {
                          handleMobileLinkClick();
                          handleSignOut();
                        }}
                        className="w-full text-left border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-6 pr-4 py-2 border-l-4 text-sm font-medium"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              {/* Sign out button for users without roles or only with invited role */}
              {(!hasRoles || isInvited) && (
                <div className="border-t border-gray-200 pt-4 pb-2">
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700 block pl-3 pr-4 py-2 border-l-4 text-base font-medium"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </nav>
 );
};

export default Navbar;
